/**
 * Planning Documents Tab (Tab 2)
 * 
 * Document repository for audit planning artifacts
 * Features:
 * - File upload (drag & drop + button)
 * - Document list with metadata
 * - Download/Delete actions
 * - File type icons
 * - API integration
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Upload, message, Spin, Dropdown, Pagination } from 'antd';
import type { UploadProps, MenuProps } from 'antd';
import { 
  FileOutlined, 
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  DownloadOutlined,
  DeleteOutlined,
  InboxOutlined,
  LoadingOutlined,
  MoreOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../../store';
import { setPlanningDocuments } from '../../../../store/slices/auditPlanSlice';
import ConfirmModal from '../../../../components/common/ConfirmModal';
import trashIcon from '../../../../assets/icons/ic-trash.svg';
import { ALLOWED_FILE_EXTENSIONS } from '../../../../utils/constants';
import {
  getPlanningDocuments,
} from '../../../../services/auditPlanService';
import {
  uploadAuditPlanningDocument,
  deleteAuditPlanningDocument,
  downloadFile,
} from '../../../../services/fileService';

const { Dragger } = Upload;

// ========== TYPE DEFINITIONS ==========

export interface PlanningDocument {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: string;
}

// ========== STYLED COMPONENTS ==========

const DocumentsContainer = styled.div`
  padding: 24px;
  background: white;
  border-radius: 8px;
`;

const UploadSection = styled.div`
  margin-bottom: 24px;
`;

const StyledDragger = styled(Dragger)`
  &.ant-upload-wrapper .ant-upload-drag {
    background: #FAFAFA;
    border: 2px dashed #D1D5DB;
    border-radius: 8px;
    transition: all 0.3s;
    
    &:hover {
      border-color: #2563EB;
    }
  }
  
  &.ant-upload-wrapper .ant-upload-drag .ant-upload-btn {
    padding: 32px 16px;
  }
  
  .ant-upload-drag-icon {
    margin-bottom: 16px;
    
    .anticon {
      font-size: 48px;
      color: #2563EB;
    }
  }
  
  .ant-upload-text {
    font-family: Inter;
    font-weight: 500;
    font-size: 16px;
    color: #1F2937;
    margin-bottom: 8px;
  }
  
  .ant-upload-hint {
    font-family: Inter;
    font-size: 14px;
    color: #6B7280;
  }
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: #1F2937;
  margin-bottom: 16px;
`;

const DocumentsWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 2px;
`;

const DocumentsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #FFFFFF;
  border-radius: 4px;
  overflow: hidden;
`;

const TableHead = styled.thead`
  background: #F8FAFC;
  
  th {
    padding: 12px 16px;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    color: #6B7280;
    text-align: left;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    
    &:first-child {
      border-left: none;
    }
  }
`;

const TableBody = styled.tbody`
  tr {
    transition: background 0.2s;
    
    &:hover {
      background: #F9FAFB;
    }
  }
  
  td {
    padding: 16px;
    font-family: Inter;
    font-size: 14px;
    color: #1F2937;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    vertical-align: middle;
    
    &:first-child {
      border-left: none;
    }
  }
  
  tr:last-child td {
    border-bottom: none;
  }
`;

const FileInfoCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FileIcon = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 20px;
  flex-shrink: 0;
  
  ${({ $type }) => {
    switch ($type) {
      case 'pdf':
        return `
          background: #FEE2E2;
          color: #DC2626;
        `;
      case 'doc':
      case 'docx':
        return `
          background: #DBEAFE;
          color: #2563EB;
        `;
      case 'xls':
      case 'xlsx':
        return `
          background: #D1FAE5;
          color: #059669;
        `;
      default:
        return `
          background: #F3F4F6;
          color: #6B7280;
        `;
    }
  }}
`;

const FileName = styled.span`
  font-weight: 500;
  color: #1F2937;
  cursor: pointer;
  
  &:hover {
    color: #2563EB;
    text-decoration: underline;
  }
`;

const FileSize = styled.span`
  color: #9CA3AF;
  font-size: 13px;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: center;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: #6B7280;
  
  &:hover {
    background: #F3F4F6;
    color: #1F2937;
  }
  
  .anticon {
    font-size: 18px;
  }
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const PaginationContainer = styled.div`
  padding: 16px;
  display: flex;
  justify-content: flex-start;
  background: #FFFFFF;
  border-top: 1px solid #E5E7EB;
`;

// ========== HELPER FUNCTIONS ==========

const getFileIcon = (type: string) => {
  switch (type) {
    case 'pdf':
      return <FilePdfOutlined />;
    case 'xls':
    case 'xlsx':
      return <FileExcelOutlined />;
    case 'doc':
    case 'docx':
      return <FileWordOutlined />;
    default:
      return <FileOutlined />;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ========== CONSTANTS ==========

const PAGE_SIZE = 10;

// ========== MAIN COMPONENT ==========

const PlanningDocumentsTab: React.FC = () => {
  // Redux state
  const dispatch = useDispatch<AppDispatch>();
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const auditPlanState = useSelector((state: RootState) => state.auditPlan);
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  
  // Get documents from Redux
  const documents = auditPlanState?.planningDocuments || [];
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  
  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<PlanningDocument | null>(null);
  
  // Calculate paginated data
  const paginatedDocuments = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return documents.slice(start, end);
  }, [documents, currentPage]);

  const totalPages = Math.ceil(documents.length / PAGE_SIZE);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    if (!selectedReport?.annualReportId) {
      return;
    }

    setLoading(true);
    try {
      const response = await getPlanningDocuments({
        annualReportId: selectedReport.annualReportId,
      });
      
      // Map API response to local format
      const mappedDocs: PlanningDocument[] = response.data.map(doc => ({
        id: doc.id,
        fileName: doc.fileName,
        fileType: doc.fileType,
        fileSize: doc.fileSize,
        uploadedBy: doc.uploadedBy,
        uploadedAt: doc.uploadedAt,
      }));
      
      // Dispatch to Redux - component subscribes to get data
      dispatch(setPlanningDocuments({
        data: mappedDocs,
        totalCount: response.totalCount,
        reportId: selectedReport.annualReportId,
      }));
      
      setCurrentPage(1); // Reset to first page when documents are refreshed
    } catch (error: any) {
      console.error('Failed to fetch documents:', error);
      message.error(error?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [selectedReport?.annualReportId, dispatch]);

  // Fetch documents when selectedReport changes
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Handle file upload
  const handleUpload = useCallback(async (file: File) => {
    if (!selectedReport?.annualReportId) {
      message.error('No annual report selected');
      return false;
    }

    setUploading(true);
    
    try {
      // console.log('📤 Uploading file:', file.name);
      
      await uploadAuditPlanningDocument(selectedReport.annualReportId, file);
      
      message.success(`${file.name} uploaded successfully`);
      
      // Refresh documents list
      await fetchDocuments();
    } catch (error: any) {
      console.error('Upload failed:', error);
      message.error(error?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
    
    return false; // Prevent default upload behavior
  }, [selectedReport?.annualReportId, fetchDocuments]);

  // Handle download
  const handleDownload = useCallback(async (doc: PlanningDocument) => {
    try {
      setDownloadingId(doc.id);
      // console.log('📥 Downloading file:', doc.fileName, 'ID:', doc.id);
      
      await downloadFile(doc.id, doc.fileName);
      
      message.success(`Downloaded ${doc.fileName}`);
    } catch (error: any) {
      console.error('Download failed:', error);
      message.error(error?.message || `Failed to download ${doc.fileName}`);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  // Open delete confirmation modal
  const openDeleteModal = useCallback((doc: PlanningDocument) => {
    setDocumentToDelete(doc);
    setDeleteModalOpen(true);
  }, []);
  
  // Close delete confirmation modal
  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
  }, []);

  // Handle delete confirmation
  const handleConfirmDelete = useCallback(async () => {
    if (!documentToDelete || !selectedReport?.annualReportId) return;
    
    setDeletingId(documentToDelete.id);
    try {
      // console.log('🗑️ Deleting file:', documentToDelete.fileName, 'ID:', documentToDelete.id);
      
      await deleteAuditPlanningDocument(selectedReport.annualReportId, documentToDelete.id);
      
      message.success('Document deleted successfully');
      closeDeleteModal();
      
      // Refresh documents list
      await fetchDocuments();
    } catch (error: any) {
      console.error('Delete failed:', error);
      message.error(error?.message || 'Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  }, [documentToDelete, selectedReport?.annualReportId, closeDeleteModal, fetchDocuments]);

  // Generate dropdown menu items for a document
  const getDocumentMenuItems = useCallback((doc: PlanningDocument): MenuProps['items'] => {
    return [
      {
        key: 'download',
        label: 'Download',
        icon: <DownloadOutlined />,
        disabled: downloadingId === doc.id,
        onClick: () => handleDownload(doc),
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <DeleteOutlined />,
        danger: true,
        disabled: deletingId === doc.id,
        onClick: () => openDeleteModal(doc),
      },
    ];
  }, [downloadingId, deletingId, handleDownload, openDeleteModal]);

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    showUploadList: false,
    beforeUpload: (file) => {
      handleUpload(file);
      return false;
    },
    accept: ALLOWED_FILE_EXTENSIONS,
  };

  return (
    <DocumentsContainer>
      {/* Upload Section */}
      <UploadSection>
        <SectionTitle>Upload Documents</SectionTitle>
        <StyledDragger {...uploadProps} disabled={uploading}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {uploading ? 'Uploading...' : 'Click or drag file to this area to upload'}
          </p>
          <p className="ant-upload-hint">
            Support for PDF, Word, Excel, PowerPoint, Visio, and other document formats. Max file size: 50MB
          </p>
        </StyledDragger>
      </UploadSection>

      {/* Documents List */}
      <div>
        <SectionTitle>Documents Repository ({documents.length})</SectionTitle>
        {loading ? (
          <LoadingWrapper>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          </LoadingWrapper>
        ) : documents.length > 0 ? (
          <DocumentsWrapper>
            <DocumentsTable>
              <TableHead>
                <tr>
                  <th style={{ width: 'auto' }}>File name</th>
                  <th style={{ width: '15%' }}>Uploaded by</th>
                  <th style={{ width: '20%' }}>Upload date</th>
                  <th style={{ width: '10%' }}>Size</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
                </tr>
              </TableHead>
              <TableBody>
                {paginatedDocuments.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <FileInfoCell>
                        <FileIcon $type={doc.fileType}>
                          {getFileIcon(doc.fileType)}
                        </FileIcon>
                        <FileName onClick={() => handleDownload(doc)}>
                          {doc.fileName}
                        </FileName>
                      </FileInfoCell>
                    </td>
                    <td>{doc.uploadedBy}</td>
                    <td>{formatDate(doc.uploadedAt)}</td>
                    <td>
                      <FileSize>{formatFileSize(doc.fileSize)}</FileSize>
                    </td>
                    <td>
                      <ActionsCell>
                        <Dropdown
                          menu={{ items: getDocumentMenuItems(doc) }}
                          trigger={['click']}
                          placement="bottomRight"
                        >
                          <ActionButton disabled={!isAdmin} onClick={(e) => e.stopPropagation()}>
                            <MoreOutlined />
                          </ActionButton>
                        </Dropdown>
                      </ActionsCell>
                    </td>
                  </tr>
                ))}
              </TableBody>
            </DocumentsTable>
            
            {/* Pagination - only show when there are multiple pages */}
              <PaginationContainer>
                <Pagination
                  current={currentPage}
                  total={documents.length}
                  pageSize={PAGE_SIZE}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showTotal={() => `${currentPage} of ${totalPages}`}
                />
              </PaginationContainer>
          </DocumentsWrapper>
        ) : (
          <EmptyStateWrapper>
            <EmptyStateText>No documents uploaded yet</EmptyStateText>
          </EmptyStateWrapper>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        icon={trashIcon}
        title="Delete Document"
        description={
          <>
            Are you sure you want to delete <strong>{documentToDelete?.fileName}</strong>?
            <br />
            This action cannot be undone.
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={deletingId === documentToDelete?.id}
      />
    </DocumentsContainer>
  );
};

export default PlanningDocumentsTab;

